// /w/:wid/map — the world map (Phase 2; Slate & Fire art, Oct 2026). The canvas is drawn by src/map/render.ts from the
// assembled world blob (kept on the device, checked every five minutes), the terrain noise and village/get (your cities, your guild's diplomacy,
// your marches). A second, transparent canvas over it is the army layer (Oct 15 2026): the marches' walking squads (or
// their fallback arrows) and countdowns, repainted at about 12 fps while a squad is walking, once a second when only
// arrows show, and with every base frame of a gesture, so the two never part. HTML over it: the X/Y search plate, the recenter / layers / bookmarks discs, the MARCHES chip, the
// REALM minimap medallion (region) or the GUILDS ON THE MAP legend (realm), and the docked city sheet. Gestures: drag
// to pan (with a little inertia), pinch or wheel to zoom, double-tap to zoom in, tap a castle for its sheet (another
// player's also lights that player's other cities). Keys on the focused map: arrows pan (Shift: further), + and − zoom,
// Home goes to your city, Escape lets go of the selected city, 1–4 are the close view, the region (the map's opening
// zoom, as the Region button), the realm (as the Realm button) and the whole world.
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useNavigate, useParams, useSearchParams } from "react-router-dom";
import { useQueryClient } from "@tanstack/react-query";
import { api } from "@/api/client";
import { Page, type HeaderSpec } from "@/app/Page";
import { cityMeta, menuBadgesOf, navBadgesOf } from "@/app/WorldPage";
import { Disc, cx } from "@/kit/primitives";
import { IconBookmark, IconCrosshair, IconLayers, IconSearch, IconSwords, IconChevronDown, IconChevronRight, IconChevronUp } from "@/kit/icons";
import { useSession } from "@/state/session";
import { ownVillages, presenceVillageId, presenceVillages, useInit, useVillage } from "@/state/queries";
import { mapKeys, useBookmarks, useGuildRankings, useGuildTags, usePlayerInfo, useVillageInfo, useWorldMap } from "@/state/mapQueries";
import type { PlayerInfoResult } from "@/api/types";
import { serverNow, useNow } from "@/state/clock";
import { usePrompts } from "@/state/prompts";
import { cityIndex, guildCounts, type Cell, type WorldMap } from "@/map/chunks";
import { TerrainCache } from "@/map/terrain";
import { pxToCell, wrap, visibleCells, type View } from "@/map/geo";
import { REALM_GREY, REALM_PALETTE, REL_COLORS, relationOf, relationsOf } from "@/map/relations";
import { MapRenderer, REGION_MIN, S_MAX, S_MIN, bakeSelectionAura, fmtLeft, loadMapAssets, type GuildMark, type March, type MapAssets, type NamePlate, type Scene } from "@/map/render";
import { ARMY_FPS, marchEnds, marchKind } from "@/map/army";
import { useVtMarks, useVtOn } from "@/state/vt";
import { readLayers, writeLayers, type Layers } from "@/map/layers";
import { movements } from "@/game/city";
import { parseCoords, plural } from "@/game/format";
import { CitySwipe } from "@/kit/CitySwipe";
import { CitySheet, LayersSheet, BookmarksSheet, MapCitySwitcher, type SheetTarget } from "./MapSheets";
import "./map.css";

const HOME_SCALE = 44; // CSS px per cell when the map opens on your city (the district view)
const REALM_SCALE = 6;
/** How long the loader waits for the first view's castles before showing the map anyway (they then appear as they load). */
const SPRITE_WAIT_MS = 5000;

/** localStorage, or null where the browser refuses it (private mode). */
function deviceStore(): Storage | null {
  try {
    return typeof localStorage !== "undefined" ? localStorage : null;
  } catch {
    return null;
  }
}

/** the map tools menu's open/closed state on this device (closed unless the player left it open) */
const TOOLS_KEY = "valor-web:map-tools-open";
function readToolsOpen(store: Storage | null): boolean {
  try { return store?.getItem(TOOLS_KEY) === "1"; } catch { return false; }
}
function writeToolsOpen(store: Storage | null, open: boolean) {
  try { store?.setItem(TOOLS_KEY, open ? "1" : "0"); } catch { /* private mode: just not remembered */ }
}

export function MapScreen() {
  const { wid: widP } = useParams();
  const wid = Number(widP);
  const session = useSession();
  const init = useInit();
  const setVillage = useSession((s) => s.setVillage);
  // the city the map belongs to: the remembered one, else the first the game lists for this world (as Empire does)
  const vid = useMemo(() => {
    if (session.vid) return session.vid;
    const ids = presenceVillages(init.data?.world, wid).map(presenceVillageId).filter((x): x is number => !!x);
    return ids[0] ?? null;
  }, [session.vid, init.data, wid]);
  useEffect(() => {
    if (!session.vid && vid) setVillage(vid);
  }, [session.vid, vid, setVillage]);
  const nav = useNavigate();
  const [params, setParams] = useSearchParams();
  const qc = useQueryClient();
  const q = useVillage(wid, vid);
  const v = q.data;
  const size = v?.world_width;
  const world = useWorldMap(wid, size);
  const guilds = useGuildRankings(wid, !!v);
  const bookmarks = useBookmarks(wid);
  const [assets, setAssets] = useState<MapAssets | null>(null);
  const [switcher, setSwitcher] = useState(false);
  const [layersOpen, setLayersOpen] = useState(false);
  const [marksOpen, setMarksOpen] = useState(() => params.get("bookmarks") === "1");
  // the saved switches; a value from before the Slate & Fire map loses its retired "washes" key here (map/layers.ts)
  const [layers, setLayers] = useState<Layers>(() => readLayers(deviceStore()));
  // the map tools menu (Ben, Oct 15 2026): one small arrow over go-to-city / Layers / Bookmarks; closed by default and
  // remembered on this device
  const [toolsOpen, setToolsOpen] = useState(() => readToolsOpen(deviceStore()));
  const toolsToggleRef = useRef<HTMLButtonElement>(null);
  useEffect(() => { writeToolsOpen(deviceStore(), toolsOpen); }, [toolsOpen]);
  const [selected, setSelected] = useState<[number, number] | null>(null);
  // the march the player tapped last: drawn above the others (two waves to one city overlap)
  const [frontMarch, setFrontMarch] = useState<string | null>(null);
  const [isolate, setIsolate] = useState<number | null>(null);
  const [names, setNames] = useState<Map<string, NamePlate>>(() => new Map());
  const [regime, setRegime] = useState<"region" | "realm">("region");
  const [centre, setCentre] = useState<[number, number]>([0, 0]);
  const now = useNow();

  useEffect(() => {
    let alive = true;
    loadMapAssets().then((a) => alive && setAssets(a)).catch(() => usePrompts.getState().toast("The map's art didn't load. Pull to refresh."));
    return () => { alive = false; };
  }, []);
  useEffect(() => {
    writeLayers(deviceStore(), layers);
  }, [layers]);

  const home = useMemo(() => parseCoords(v?.village?.coords), [v?.village?.coords]);
  const own = useMemo(() => ownVillages(v, session.uid), [v, session.uid]);
  const relations = useMemo(() => relationsOf(v, session.uid), [v, session.uid]);
  // terrain in 16-cell blocks (a block is ~20k noise samples, well under a frame; the game's own 64-cell chunks would hitch)
  const terrain = useMemo(() => (v?.map_features && size ? new TerrainCache(wid, size, v.map_features, 16) : null), [wid, size, v?.map_features]);
  const cities = useMemo(() => (world.data ? cityIndex(world.data) : new Map<string, Cell>()), [world.data]);
  const cityList = useMemo(() => [...cities.values()], [cities]);
  const cityVersion = useMemo(() => {
    let h = 0;
    for (const c of cityList) h = (h * 31 + c.x * 1024 + c.y + c.level * 7) | 0;
    return h;
  }, [cityList]);
  const counts = useMemo(() => guildCounts(cityList), [cityList]);
  // realm colours: relationships first, then the biggest other guilds get the palette
  const realmColors = useMemo(() => {
    const m = new Map<number, string>();
    let i = 0;
    for (const { gid } of counts) {
      if (gid === 0) continue;
      if (gid === relations.gid) m.set(gid, REL_COLORS.guild.body!);
      else if (relations.enemies.has(gid)) m.set(gid, REL_COLORS.enemy.body!);
      else if (relations.allies.has(gid)) m.set(gid, REL_COLORS.ally.body!);
      else if (relations.naps.has(gid)) m.set(gid, REL_COLORS.nap.body!);
      else if (i < REALM_PALETTE.length) m.set(gid, REALM_PALETTE[i++]);
      else m.set(gid, REALM_GREY);
    }
    return m;
  }, [counts, relations]);
  // a city in no guild wears the unaligned colour the castles use; other guilds keep their place in the palette
  const realmColor = useCallback((c: Cell) => (relations.own.has(`${c.x},${c.y}`) ? REL_COLORS.you.body! : c.guildId > 0 ? realmColors.get(c.guildId) ?? REALM_GREY : REL_COLORS.unaligned.body!), [relations, realmColors]);

  // the nameplates the All nameplates layer hangs: your own cities' (always known), the others' as they are read. A selected
  // city gets no plate of its own (Ben, Oct 12 2026: its glow and its sheet say which city it is).
  const allNames = useMemo(() => {
    const m = new Map(names);
    for (const c of own) m.set(c.coords, { name: c.city_name, sub: `R${c.continent}`, own: true });
    return m;
  }, [names, own]);

  // Selecting another player's city — not yours, not a barbarian's — lights that player's other cities in their own colours
  // (Ben, Oct 12 2026): the owner from the city's village/info, their cities from player/info (the player sheet's query,
  // cached five minutes). While a newly tapped city's village/info is on its way, the player whose cities are lit keeps
  // them lit if the tapped city is one of his, so going from one of a player's cities to the next doesn't flicker.
  const selInfo = useVillageInfo(wid, selected);
  const selKey = selected ? `${selected[0]},${selected[1]}` : null;
  const selectedCell = selKey ? cities.get(selKey) ?? null : null;
  const selRel = selectedCell ? relationOf(selectedCell, relations) : null;
  const litOwner = useRef<number | null>(null);
  const ownerUid = useMemo(() => {
    if (!selKey || !selectedCell || selRel === "you" || selRel === "barbarian") return null;
    const d = selInfo.data?.city_details?.[selKey];
    if (d) {
      const uid = d.target_char?.user_id ?? 0;
      return !d.target_village?.is_barbarian && uid > 0 && uid !== session.uid ? uid : null;
    }
    const prev = litOwner.current;
    if (!selInfo.isPending || !prev) return null;
    const theirs = qc.getQueryData<PlayerInfoResult>(mapKeys.player(wid, prev))?.player_details?.[String(prev)]?.target_char?.villages;
    return theirs?.some((c) => sameCell(c.coords, selKey)) ? prev : null;
  }, [selKey, selectedCell, selRel, selInfo.data, selInfo.isPending, session.uid, qc, wid]);
  const ownerInfo = usePlayerInfo(wid, ownerUid);
  const ownerCities = useMemo(() => {
    if (!ownerUid || !selKey) return null;
    const out = new Set<string>();
    for (const c of ownerInfo.data?.player_details?.[String(ownerUid)]?.target_char?.villages ?? []) {
      const xy = parseCoords(c.coords);
      const k = xy ? `${xy[0]},${xy[1]}` : null;
      if (k && k !== selKey) out.add(k);
    }
    return out.size ? out : null;
  }, [ownerUid, selKey, ownerInfo.data]);
  useEffect(() => {
    litOwner.current = ownerCities ? ownerUid : null;
  }, [ownerUid, ownerCities]);

  // your marches: both kinds of return are `return` (grey), and a return runs from the city it is returning from, home —
  // the game keeps its origin and target as they were (map/army.ts marchEnds)
  const marches = useMemo<March[]>(() => {
    if (!v || !home) return [];
    const { incoming, outgoing } = movements(v);
    const out: March[] = [];
    for (const e of outgoing) {
      const other = parseCoords(e.other.coords);
      const kind = marchKind(e.kind);
      if (other) out.push({ ...marchEnds("out", home, other, kind), startMs: e.startMs, endMs: e.endMs, kind, incoming: false, id: `out:${e.m.id}` });
    }
    for (const e of incoming) {
      const other = parseCoords(e.other.coords);
      const kind = marchKind(e.kind);
      if (other) out.push({ ...marchEnds("in", home, other, kind), startMs: e.startMs, endMs: e.endMs, kind, incoming: true, id: `in:${e.m.id}` });
    }
    return out;
  }, [v, home]);

  // ---------------------------------------------------------------- the canvas and its view
  const wrapRef = useRef<HTMLDivElement>(null);
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const armyRef = useRef<HTMLCanvasElement>(null);
  const viewRef = useRef<View>({ cx: 0, cy: 0, s: HOME_SCALE });
  const rendererRef = useRef<MapRenderer | null>(null);
  const sceneRef = useRef<Scene | null>(null);
  const placedRef = useRef(false);
  const dirtyRef = useRef(true);
  const settledAtRef = useRef(0);
  const gestureRef = useRef(false);
  const rafRef = useRef(0);

  // the renderer lives as long as its art; unmounting releases its sprites, tiles and caches (StrictMode's development
  // re-mount gets a fresh one)
  useEffect(() => {
    if (!assets) return;
    const r = new MapRenderer(assets);
    // a castle or glow arriving (or failing) redraws the map
    r.onChange = () => { dirtyRef.current = true; };
    r.debug = !!(window as unknown as { __VALOR_MAP_DEBUG__?: boolean }).__VALOR_MAP_DEBUG__;
    rendererRef.current = r;
    dirtyRef.current = true;
    return () => {
      r.dispose();
      if (rendererRef.current === r) rendererRef.current = null;
    };
  }, [assets]);
  // the loader stays up until the first view's castles have arrived (or SPRITE_WAIT_MS has passed)
  const [spritesIn, setSpritesIn] = useState(false);
  const spritesInRef = useRef(false);
  const worldInRef = useRef(false);
  worldInRef.current = !!world.data;

  // Valor Tools' guild claims and op targets (Oct 15 2026; only for players it's switched on for)
  const vtOn = useVtOn(wid);
  const vtMarks = useVtMarks(wid);
  const guildMarks = useMemo<GuildMark[]>(() => {
    const d = vtMarks.data;
    if (!d?.on) return [];
    const out: GuildMark[] = [], seen = new Set<string>();
    for (const c of d.claims ?? []) { const k = `${c.x},${c.y}`; if (seen.has(k)) continue; seen.add(k); out.push({ x: c.x, y: c.y, kind: c.mine ? "mine" : "claim" }); }
    for (const o of d.ops ?? []) out.push({ x: o.x, y: o.y, kind: "op" });
    return out;
  }, [vtMarks.data]);

  // the scene the renderer reads each frame
  sceneRef.current = useMemo<Scene>(() => ({
    size: size ?? 1024,
    cities,
    cityList,
    terrain,
    relations,
    realmColor,
    isolateGid: isolate,
    selected,
    ownerCities,
    home,
    names: allNames,
    marches,
    frontMarch,
    now,
    layers,
    cityVersion,
    guildMarks,
  }), [size, cities, cityList, terrain, relations, realmColor, isolate, selected, ownerCities, home, allNames, marches, frontMarch, now, layers, cityVersion, guildMarks]);
  dirtyRef.current = true;

  // first placement: your city (or ?x&y from a link), once the city is known
  useEffect(() => {
    if (placedRef.current || !home) return;
    const px = Number(params.get("x"));
    const py = Number(params.get("y"));
    const target: [number, number] = Number.isFinite(px) && Number.isFinite(py) && params.has("x") ? [px, py] : home;
    viewRef.current = { cx: target[0] + 0.5, cy: target[1] + 0.5, s: HOME_SCALE };
    placedRef.current = true;
    if (params.has("x")) setSelected(target);
    dirtyRef.current = true;
    // from the city view (zoomed out past its overview): carry the zoom-out on — start close on the city, ease out to the
    // map's opening zoom. Any touch takes over (a gesture sets the view itself).
    if (params.get("from") === "city") {
      setParams((p) => { p.delete("from"); return p; }, { replace: true });
      if (typeof matchMedia !== "undefined" && matchMedia("(prefers-reduced-motion: reduce)").matches) return;
      const s0 = Math.min(S_MAX, HOME_SCALE * 2.6);
      const t0 = performance.now();
      const ms = 520;
      viewRef.current = { ...viewRef.current, s: s0 };
      let last = s0;
      const step = (now: number) => {
        if (gestureRef.current || Math.abs(viewRef.current.s - last) > 1e-6) return; // the player took over
        const k = Math.min(1, (now - t0) / ms);
        const e = 1 - Math.pow(1 - k, 3);
        last = s0 + (HOME_SCALE - s0) * e;
        viewRef.current = { ...viewRef.current, s: last };
        settledAtRef.current = now;
        dirtyRef.current = true;
        if (k < 1) requestAnimationFrame(step);
      };
      requestAnimationFrame(step);
    }
  }, [home, params, setParams]);

  const requestDraw = useCallback(() => { dirtyRef.current = true; }, []);

  // The draw loop. One frame decides both canvases from one snapshot of the view, size and pixel ratio:
  //   base — redraws when something changed, every frame during a gesture, once a second otherwise (the selection glow is
  //     still; the countdowns moved to the army layer, but the base keeps its cadence);
  //   army layer — with every base frame (so the two never part during a pan, zoom, fly-to or resize); otherwise at about
  //     ARMY_FPS while any squad is walking, once a second while only arrows show, and on demand while a crowded march
  //     waits out its clearance. Under reduced motion the squads hold a pose, so once a second is enough.
  // Hidden tab: the layer is cleared and left alone (no frames arrive anyway); on return it draws at the current server
  // time, nothing replayed. Marches off: the layer clears itself (drawArmyLayer).
  useEffect(() => {
    let lastTick = 0;
    let lastArmy = -Infinity;
    let armyDirty = true;
    const reduce = typeof matchMedia !== "undefined" ? matchMedia("(prefers-reduced-motion: reduce)") : null;
    const clearArmy = () => {
      const c = armyRef.current;
      if (!c) return;
      const g = c.getContext("2d");
      if (g) { g.setTransform(1, 0, 0, 1, 0, 0); g.clearRect(0, 0, c.width, c.height); }
    };
    const onVisible = () => {
      if (document.visibilityState === "hidden") clearArmy();
      else { armyDirty = true; dirtyRef.current = true; }
    };
    document.addEventListener("visibilitychange", onVisible);
    const loop = (t: number) => {
      rafRef.current = requestAnimationFrame(loop);
      const canvas = canvasRef.current;
      const army = armyRef.current;
      const wrapEl = wrapRef.current;
      const r = rendererRef.current;
      const scene = sceneRef.current;
      if (!canvas || !wrapEl || !r || !scene || !placedRef.current) return;
      if (document.visibilityState === "hidden") return;
      const baseDue = dirtyRef.current || gestureRef.current || t - lastTick >= 1000;
      const reduced = !!reduce?.matches;
      const fps = reduced ? 1 : ARMY_FPS;
      const armyDue = baseDue || armyDirty || ((r.armyWalking || r.armyRestorePending) && t - lastArmy >= 1000 / fps) || t - lastArmy >= 1000;
      if (!baseDue && !armyDue) return;
      const dpr = Math.min(3, window.devicePixelRatio || 1);
      const w = wrapEl.clientWidth;
      const h = wrapEl.clientHeight;
      const size = (c: HTMLCanvasElement) => {
        if (c.width !== Math.round(w * dpr) || c.height !== Math.round(h * dpr)) {
          c.width = Math.round(w * dpr);
          c.height = Math.round(h * dpr);
          c.style.width = `${w}px`;
          c.style.height = `${h}px`;
          return true;
        }
        return false;
      };
      const resized = size(canvas);
      if (army) size(army);
      const now = serverNow();
      if (baseDue || resized) {
        lastTick = t;
        dirtyRef.current = false;
        const ctx = canvas.getContext("2d");
        if (!ctx) return;
        const settled = !gestureRef.current && performance.now() - settledAtRef.current > 140;
        r.draw(ctx, w, h, dpr, viewRef.current, { ...scene, now }, settled);
        if (!spritesInRef.current && r.pendingSprites === 0 && worldInRef.current) {
          spritesInRef.current = true;
          setSpritesIn(true);
        }
        const reg = viewRef.current.s >= REGION_MIN ? "region" : "realm";
        setRegime((cur) => (cur === reg ? cur : reg));
        const c: [number, number] = [Math.floor(wrap(viewRef.current.cx, scene.size)), Math.floor(wrap(viewRef.current.cy, scene.size))];
        setCentre((cur) => (cur[0] === c[0] && cur[1] === c[1] ? cur : c));
      }
      // the army layer, from the same view, size and server time as the base frame it sits over
      const actx = army?.getContext("2d");
      if (actx) {
        lastArmy = t;
        armyDirty = false;
        r.drawArmyLayer(actx, w, h, dpr, viewRef.current, { ...scene, now }, t, reduced);
      }
    };
    rafRef.current = requestAnimationFrame(loop);
    const ro = new ResizeObserver(() => { dirtyRef.current = true; });
    if (wrapRef.current) ro.observe(wrapRef.current);
    return () => { cancelAnimationFrame(rafRef.current); ro.disconnect(); document.removeEventListener("visibilitychange", onVisible); clearArmy(); };
  }, []);

  // ---------------------------------------------------------------- gestures
  const pointers = useRef(new Map<number, { x: number; y: number }>());
  const drag = useRef<{ x: number; y: number; t: number; vx: number; vy: number; moved: boolean; startX: number; startY: number } | null>(null);
  const pinch = useRef<{ d0: number; s0: number; mx: number; my: number } | null>(null);
  const lastTap = useRef(0);
  const inertia = useRef(0);

  const zoomAt = useCallback((factor: number, px: number, py: number) => {
    const wrapEl = wrapRef.current;
    if (!wrapEl) return;
    const w = wrapEl.clientWidth;
    const h = wrapEl.clientHeight;
    const size = sceneRef.current?.size ?? 1024;
    const vw = viewRef.current;
    const [cx, cy] = pxToCell(vw, w, h, size, px, py);
    const s = Math.min(S_MAX, Math.max(S_MIN, vw.s * factor));
    // keep the cell under (px, py) where it is
    const ncx = cx - (px - w / 2) / s;
    const ncy = cy + (py - h / 2) / s;
    viewRef.current = { cx: wrap(ncx, size), cy: wrap(ncy, size), s };
    settledAtRef.current = performance.now();
    dirtyRef.current = true;
  }, []);

  const onPointerDown = (e: React.PointerEvent) => {
    (e.target as HTMLElement).setPointerCapture?.(e.pointerId);
    canvasRef.current?.focus({ preventScroll: true }); // so the map's keys work after a click
    pointers.current.set(e.pointerId, { x: e.clientX, y: e.clientY });
    cancelAnimationFrame(inertia.current);
    gestureRef.current = true;
    if (pointers.current.size === 1) {
      drag.current = { x: e.clientX, y: e.clientY, t: performance.now(), vx: 0, vy: 0, moved: false, startX: e.clientX, startY: e.clientY };
      pinch.current = null;
    } else if (pointers.current.size === 2) {
      const [a, b] = [...pointers.current.values()];
      const rect = wrapRef.current!.getBoundingClientRect();
      pinch.current = { d0: Math.hypot(a.x - b.x, a.y - b.y), s0: viewRef.current.s, mx: (a.x + b.x) / 2 - rect.left, my: (a.y + b.y) / 2 - rect.top };
      drag.current = null;
    }
  };
  const onPointerMove = (e: React.PointerEvent) => {
    if (!pointers.current.has(e.pointerId)) return;
    pointers.current.set(e.pointerId, { x: e.clientX, y: e.clientY });
    const size = sceneRef.current?.size ?? 1024;
    if (pinch.current && pointers.current.size >= 2) {
      const [a, b] = [...pointers.current.values()];
      const d = Math.hypot(a.x - b.x, a.y - b.y);
      const rect = wrapRef.current!.getBoundingClientRect();
      const mx = (a.x + b.x) / 2 - rect.left;
      const my = (a.y + b.y) / 2 - rect.top;
      const target = Math.min(S_MAX, Math.max(S_MIN, pinch.current.s0 * (d / Math.max(1, pinch.current.d0))));
      // zoom about the midpoint, then follow the midpoint's drift
      zoomAt(target / viewRef.current.s, mx, my);
      const vw = viewRef.current;
      viewRef.current = { ...vw, cx: wrap(vw.cx - (mx - pinch.current.mx) / vw.s, size), cy: wrap(vw.cy + (my - pinch.current.my) / vw.s, size) };
      pinch.current.mx = mx;
      pinch.current.my = my;
      dirtyRef.current = true;
      return;
    }
    const d = drag.current;
    if (!d) return;
    const dx = e.clientX - d.x;
    const dy = e.clientY - d.y;
    if (!d.moved && Math.hypot(e.clientX - d.startX, e.clientY - d.startY) > 6) d.moved = true;
    if (!d.moved) return;
    const t = performance.now();
    const dt = Math.max(1, t - d.t);
    d.vx = 0.7 * d.vx + 0.3 * (dx / dt);
    d.vy = 0.7 * d.vy + 0.3 * (dy / dt);
    d.x = e.clientX; d.y = e.clientY; d.t = t;
    const vw = viewRef.current;
    viewRef.current = { ...vw, cx: wrap(vw.cx - dx / vw.s, size), cy: wrap(vw.cy + dy / vw.s, size) };
    dirtyRef.current = true;
  };
  const endGesture = (e: React.PointerEvent) => {
    pointers.current.delete(e.pointerId);
    const size = sceneRef.current?.size ?? 1024;
    if (pointers.current.size === 0) {
      gestureRef.current = false;
      settledAtRef.current = performance.now();
      const d = drag.current;
      drag.current = null;
      pinch.current = null;
      if (d && !d.moved) {
        const rect = wrapRef.current!.getBoundingClientRect();
        const px = e.clientX - rect.left;
        const py = e.clientY - rect.top;
        const t = performance.now();
        if (t - lastTap.current < 300) {
          lastTap.current = 0;
          zoomAt(viewRef.current.s < REGION_MIN ? (REGION_MIN * 1.8) / viewRef.current.s : 1.8, px, py);
          return;
        }
        lastTap.current = t;
        tapAt(px, py);
      } else if (d) {
        // inertia
        let vx = d.vx; let vy = d.vy; let last = performance.now();
        const step = () => {
          const t = performance.now();
          const dt = t - last; last = t;
          vx *= Math.pow(0.9, dt / 16); vy *= Math.pow(0.9, dt / 16);
          if (Math.hypot(vx, vy) < 0.02) return;
          const vw = viewRef.current;
          viewRef.current = { ...vw, cx: wrap(vw.cx - (vx * dt) / vw.s, size), cy: wrap(vw.cy + (vy * dt) / vw.s, size) };
          dirtyRef.current = true;
          inertia.current = requestAnimationFrame(step);
        };
        inertia.current = requestAnimationFrame(step);
      }
    } else if (pointers.current.size === 1) {
      const [p] = [...pointers.current.values()];
      drag.current = { x: p.x, y: p.y, t: performance.now(), vx: 0, vy: 0, moved: true, startX: p.x, startY: p.y };
      pinch.current = null;
    }
  };
  const onWheel = (e: React.WheelEvent) => {
    const rect = wrapRef.current!.getBoundingClientRect();
    zoomAt(Math.pow(1.0015, -e.deltaY), e.clientX - rect.left, e.clientY - rect.top);
  };

  const tapAt = (px: number, py: number) => {
    const wrapEl = wrapRef.current;
    const r = rendererRef.current;
    const scene = sceneRef.current;
    if (!wrapEl || !r || !scene) return;
    // a tap on a march's arrow or countdown brings that march to the front (it doesn't change the selected city)
    const march = r.hitMarch(wrapEl.clientWidth, wrapEl.clientHeight, viewRef.current, scene, px, py);
    if (march) {
      setFrontMarch(march);
      dirtyRef.current = true;
      return;
    }
    const hit = r.hitTest(wrapEl.clientWidth, wrapEl.clientHeight, viewRef.current, scene, px, py);
    if (hit) {
      setSelected([hit.x, hit.y]);
      if (viewRef.current.s < REGION_MIN) {
        // from the realm, a tap on a dot flies to it
        flyTo([hit.x, hit.y], REGION_MIN * 1.8);
      }
    } else {
      setSelected(null);
    }
    dirtyRef.current = true;
  };

  const flyTo = useCallback((cell: [number, number], s?: number) => {
    const size = sceneRef.current?.size ?? 1024;
    viewRef.current = { cx: wrap(cell[0] + 0.5, size), cy: wrap(cell[1] + 0.5, size), s: s ?? Math.max(viewRef.current.s, REGION_MIN * 1.8) };
    settledAtRef.current = performance.now();
    dirtyRef.current = true;
  }, []);

  const goHome = () => home && flyTo(home, Math.max(viewRef.current.s, HOME_SCALE));
  const toRealm = () => { viewRef.current = { ...viewRef.current, s: REALM_SCALE }; settledAtRef.current = performance.now(); dirtyRef.current = true; };
  const toRegion = () => { viewRef.current = { ...viewRef.current, s: HOME_SCALE }; settledAtRef.current = performance.now(); dirtyRef.current = true; };

  // ---------------------------------------------------------------- keys (the focused map; the reference's controls)
  /** 1–4: the closest zoom, the region (the opening zoom, as the Region button), the realm (as the Realm button) and the
   *  whole world (the reference's close / district / region / realm views, in this map's own terms). */
  const zoomPreset = (k: "close" | "region" | "realm" | "world") => {
    const wrapEl = wrapRef.current;
    if (!wrapEl) return;
    const size = sceneRef.current?.size ?? 1024;
    const s = k === "close" ? S_MAX : k === "region" ? HOME_SCALE : k === "realm" ? REALM_SCALE : Math.min(wrapEl.clientWidth, wrapEl.clientHeight) / (size * 1.08);
    cancelAnimationFrame(inertia.current);
    viewRef.current = { ...viewRef.current, s: Math.min(S_MAX, Math.max(S_MIN, s)) };
    settledAtRef.current = performance.now();
    dirtyRef.current = true;
  };
  const onKeyDown = (e: React.KeyboardEvent) => {
    if (e.altKey || e.ctrlKey || e.metaKey) return;
    const wrapEl = wrapRef.current;
    if (!wrapEl) return;
    const size = sceneRef.current?.size ?? 1024;
    // north is up: ArrowUp looks north (y grows)
    const pan = ({ ArrowLeft: [-1, 0], ArrowRight: [1, 0], ArrowUp: [0, 1], ArrowDown: [0, -1] } as Record<string, [number, number]>)[e.key];
    if (pan) {
      e.preventDefault();
      cancelAnimationFrame(inertia.current);
      const vw = viewRef.current;
      const step = (e.shiftKey ? 180 : 70) / vw.s;
      viewRef.current = { ...vw, cx: wrap(vw.cx + pan[0] * step, size), cy: wrap(vw.cy + pan[1] * step, size) };
      settledAtRef.current = performance.now();
      dirtyRef.current = true;
    } else if (e.key === "+" || e.key === "=") {
      e.preventDefault();
      zoomAt(1.4, wrapEl.clientWidth / 2, wrapEl.clientHeight / 2);
    } else if (e.key === "-" || e.key === "_") {
      e.preventDefault();
      zoomAt(1 / 1.4, wrapEl.clientWidth / 2, wrapEl.clientHeight / 2);
    } else if (e.key === "Home") {
      e.preventDefault();
      goHome();
    } else if (e.key === "Escape") {
      if (selected) {
        e.preventDefault();
        setSelected(null);
        dirtyRef.current = true;
      }
    } else if (!e.shiftKey) {
      const preset = ({ "1": "close", "2": "region", "3": "realm", "4": "world" } as const)[e.key as "1" | "2" | "3" | "4"];
      if (preset) {
        e.preventDefault();
        zoomPreset(preset);
      }
    }
  };

  // ---------------------------------------------------------------- names for visible cities (village/info, a few at a time)
  const naming = useRef(new Set<string>());
  useEffect(() => {
    if (!world.data || !size) return;
    const id = window.setInterval(() => {
      const wrapEl = wrapRef.current;
      const vw = viewRef.current;
      if (!wrapEl || vw.s < 56 || gestureRef.current) return;
      const vis = visibleCells(vw, wrapEl.clientWidth, wrapEl.clientHeight, 0);
      const want: Cell[] = [];
      for (let y = vis.y0; y <= vis.y1; y++) for (let x = vis.x0; x <= vis.x1; x++) {
        const c = cities.get(`${wrap(x, size)},${wrap(y, size)}`);
        if (!c || c.barbarian) continue;
        const k = `${c.x},${c.y}`;
        if (allNames.has(k) || naming.current.has(k)) continue;
        want.push(c);
      }
      want.sort((a, b) => Math.hypot(a.x + 0.5 - vw.cx, a.y + 0.5 - vw.cy) - Math.hypot(b.x + 0.5 - vw.cx, b.y + 0.5 - vw.cy));
      for (const c of want.slice(0, Math.max(0, 3 - naming.current.size))) {
        const k = `${c.x},${c.y}`;
        naming.current.add(k);
        qc.fetchQuery({ queryKey: mapKeys.info(wid, c.x, c.y), queryFn: () => api.villageInfo({ wid }, c.x, c.y), staleTime: 10 * 60_000 })
          .then((r) => {
            const d = r.city_details?.[k];
            if (!d) return;
            const tag = guilds.data?.get(d.target_char.gid)?.tag;
            const sub = d.target_char.gid > 0 ? `${tag ? `[${tag}] ` : ""}${d.target_char.guild_info?.name ?? ""} · ${d.target_village.points.toLocaleString()} pts` : `${d.target_char.login} · ${d.target_village.points.toLocaleString()} pts`;
            setNames((m) => new Map(m).set(k, { name: cleanName(d.target_village.city_name), sub }));
          })
          .catch(() => setNames((m) => new Map(m).set(k, { name: "" })))
          .finally(() => naming.current.delete(k));
      }
    }, 450);
    return () => window.clearInterval(id);
  }, [world.data, size, cities, allNames, wid, qc, guilds.data]);

  // ---------------------------------------------------------------- X/Y search
  const [searching, setSearching] = useState(false);
  const [sx, setSx] = useState("");
  const [sy, setSy] = useState("");
  const goCoords = () => {
    const x = Number(sx); const y = Number(sy);
    if (!size || !Number.isInteger(x) || !Number.isInteger(y) || x < 0 || y < 0 || x >= size || y >= size) {
      usePrompts.getState().toast(`Enter X and Y between 0 and ${(size ?? 1024) - 1}.`);
      return;
    }
    setSearching(false);
    flyTo([x, y]);
    setSelected(cities.has(`${x},${y}`) ? [x, y] : null);
    setParams((p) => { p.set("x", String(x)); p.set("y", String(y)); return p; }, { replace: true });
  };

  // ---------------------------------------------------------------- derived bits for the overlays
  const soonest = useMemo(() => {
    const atk = marches.filter((m) => m.kind === "attack" && m.endMs > now);
    const all = marches.filter((m) => m.endMs > now);
    if (!all.length) return null;
    const next = all.reduce((a, b) => (a.endMs < b.endMs ? a : b));
    const parts: string[] = [];
    const inc = atk.filter((m) => m.incoming).length;
    const outAtk = atk.filter((m) => !m.incoming).length;
    if (inc) parts.push(`${inc} incoming`);
    if (outAtk) parts.push(plural(outAtk, "attack"));
    const sup = all.filter((m) => m.kind === "support").length;
    if (sup) parts.push(plural(sup, "support"));
    const ret = all.filter((m) => m.kind === "return").length;
    if (ret) parts.push(plural(ret, "return"));
    return { text: `${parts.join(" · ") || plural(all.length, "march", "marches")} · lands ${fmtLeft(next.endMs - now)}`, count: all.length };
  }, [marches, now]);

  const legend = useMemo(() => {
    if (regime !== "realm" || !cityList.length) return [];
    const wrapEl = wrapRef.current;
    const vw = viewRef.current;
    const vis = wrapEl ? visibleCells(vw, wrapEl.clientWidth, wrapEl.clientHeight, 0) : null;
    const n = new Map<number, number>();
    for (const c of cityList) {
      if (c.barbarian) continue;
      if (vis) {
        const dx = ((c.x - vw.cx) % (size ?? 1024) + (size ?? 1024) * 1.5) % (size ?? 1024) - (size ?? 1024) / 2;
        const dy = ((c.y - vw.cy) % (size ?? 1024) + (size ?? 1024) * 1.5) % (size ?? 1024) - (size ?? 1024) / 2;
        if (vw.cx + dx < vis.x0 || vw.cx + dx > vis.x1 || vw.cy + dy < vis.y0 || vw.cy + dy > vis.y1) continue;
      }
      n.set(c.guildId, (n.get(c.guildId) ?? 0) + 1);
    }
    const rows = [...n.entries()].filter(([gid]) => gid > 0).sort((a, b) => b[1] - a[1]).slice(0, 7).map(([gid, k]) => ({ gid, n: k, color: realmColors.get(gid) ?? REALM_GREY }));
    if (n.get(0)) rows.push({ gid: 0, n: n.get(0)!, color: REL_COLORS.unaligned.body! });
    return rows;
  }, [regime, cityList, realmColors, size, centre]); // eslint-disable-line react-hooks/exhaustive-deps
  const legendTags = useGuildTags(wid, vid, legend.map((g) => g.gid).filter((g) => g > 0));
  const legendLabel = (gid: number) => {
    if (gid === 0) return "Unaligned";
    const g = guilds.data?.get(gid);
    const tag = legendTags.get(gid) ?? g?.tag;
    return g ? `${tag ? `[${tag}] ` : ""}${g.name}` : `Guild ${gid}`;
  };

  const sheetTarget: SheetTarget | null = selected ? { x: selected[0], y: selected[1], cell: selectedCell } : null;

  const header: HeaderSpec = {
    kind: "city",
    city: v?.village?.city_name ?? (q.isLoading ? "…" : "Map"),
    meta: cityMeta(v, own.length),
    onCity: () => setSwitcher(true),
    v,
  };

  const ready = !!assets && !!world.data && !!v;
  useEffect(() => {
    if (!ready || spritesIn) return;
    const id = window.setTimeout(() => { spritesInRef.current = true; setSpritesIn(true); }, SPRITE_WAIT_MS);
    return () => window.clearTimeout(id);
  }, [ready, spritesIn]);
  // e2e checks only (window.__VALOR_MAP_DEBUG__ set before the app loads): read-only views of the renderer and camera
  useEffect(() => {
    const r = rendererRef.current;
    if (!r?.debug) return;
    const hook = {
      view: () => ({ ...viewRef.current }),
      frame: () => r.debugFrame(),
      size: () => sceneRef.current?.size ?? null,
      tileRenders: () => r.tileRenders,
      /** repaints so far: the base canvas (terrain, castles, lines) and the army layer (Oct 15 2026) */
      draws: () => ({ base: r.baseDraws, army: r.armyDraws }),
      /** the army layer (Oct 15 2026): whether a squad is walking, whether a crowded march is waiting to get its squad
       *  back, and the marches in arrow mode because of the crowd */
      army: () => ({ walking: r.armyWalking, restorePending: r.armyRestorePending, crowded: r.crowdArrows, view: r.armyView }),
      waterways: (bounds: [number, number, number, number]) => r.waterwaysIn(...bounds),
      /** which natural terrain materials / accents loaded (Oct 14 2026), and the ground repaints for late ones */
      materials: () => {
        const m = r.assets.materials;
        return { done: !!r.assets.materialsDone, repaints: r.materialRepaints, loaded: m ? Object.entries(m).filter(([, v]) => !!v).map(([k]) => k).sort() : [] };
      },
      cacheBuilds: () => r.cacheBuilds,
      cacheKeys: () => r.cacheKeys,
      /** whether the hit mask in use for a sprite family and tier is solid at normalised (u, v) (null: none yet) */
      mask: (family: "castle" | "npc", tier: 1 | 2 | 3 | 4, u: number, v: number, key?: string) => r.maskAt(family, tier, u, v, key),
      /** painted world v2 (Oct 15 2026): whether it loaded, and which of its pieces did */
      painted: () => {
        const p = r.assets.painted;
        if (!p) return null;
        const n = (a: (unknown | null)[]) => a.filter(Boolean).length;
        return { materials: Object.entries(p.materials).filter(([, v]) => !!v).map(([k]) => k).sort(), meadow: !!p.meadow, trees: n(p.trees), rocks: n(p.rocks), reeds: n(p.reeds), shores: n(p.shores), walls: n(p.walls), bridges: n(p.bridges), pennants: n(Object.values(p.pennants)) };
      },
      /** the baked stockade glows (live canvases: teardown empties them) */
      auras: () => r.auraCanvases,
      /** the castle glows tinted for the selected city's owner's other cities (live canvases: teardown empties them) */
      tints: () => r.tintCanvases,
      /** the map's own glow bake, for any loaded image (the checks bake a castle with it and compare the shipped glow) */
      bakeAura: (image: HTMLImageElement) => bakeSelectionAura(image),
      /** the city a tap at this canvas point (CSS px) selects — the same hit test as a tap, without the march check */
      hit: (x: number, y: number) => {
        const wrapEl = wrapRef.current;
        const scene = sceneRef.current;
        if (!wrapEl || !scene) return null;
        const c = r.hitTest(wrapEl.clientWidth, wrapEl.clientHeight, viewRef.current, scene, x, y);
        return c ? [c.x, c.y] : null;
      },
    };
    (window as unknown as { __valorMap?: typeof hook }).__valorMap = hook;
    return () => { delete (window as unknown as { __valorMap?: typeof hook }).__valorMap; };
  }, [assets]);
  const bookmarked = useMemo(() => new Set((bookmarks.data?.bookmarks ?? []).flatMap((o) => Object.values(o))), [bookmarks.data]);

  return (
    <Page layout="full" header={header} badges={navBadgesOf(v)} menuBadges={menuBadgesOf(v)} notifications={v?.new_notifications ? 1 : undefined}>
      <div className={cx("map-wrap", regime)} ref={wrapRef}>
        <canvas
          ref={canvasRef}
          className="map-canvas"
          tabIndex={0}
          role="application"
          aria-roledescription="map"
          aria-keyshortcuts="ArrowUp ArrowDown ArrowLeft ArrowRight Plus - Home Escape 1 2 3 4"
          aria-label={ready ? `World ${wid} map around (${centre[0]}, ${centre[1]}). Arrow keys look around, plus and minus zoom, Home goes to your city, 1 to 4 choose the close, region, realm and whole-world views.` : "Loading the map"}
          onKeyDown={onKeyDown}
          onPointerDown={(e) => { if (searching) setSearching(false); onPointerDown(e); }}
          onPointerMove={onPointerMove}
          onPointerUp={endGesture}
          onPointerCancel={endGesture}
          onWheel={onWheel}
          onContextMenu={(e) => e.preventDefault()}
        />
        {/* the army layer: the marches' walking squads (or arrows) and countdowns, over the base canvas; no pointer of its own */}
        <canvas ref={armyRef} className="map-army" aria-hidden="true" />
        {(!ready || !spritesIn) && (
          <div className="map-loading" aria-live="polite">
            <span className="spin" /> {world.isError ? "The map didn't load." : "Unrolling the map…"}
            {world.isError && <button type="button" className="link" onClick={() => world.refetch()}>Try again</button>}
          </div>
        )}

        {/* X/Y plate */}
        <div className={cx("map-xy", searching && "editing")}>
          {!searching ? (
            <button type="button" className="map-xy-btn" onClick={() => { setSx(String(centre[0])); setSy(String(centre[1])); setSearching(true); }} aria-label={`Centre (${centre[0]}, ${centre[1]}). Search coordinates`}>
              <span className="ax">X</span><b>{centre[0]}</b><span className="dia" /><span className="ax">Y</span><b>{centre[1]}</b>
              <span className="sep" /><IconSearch className="ico" />
            </button>
          ) : (
            <form className="map-xy-form" onSubmit={(e) => { e.preventDefault(); goCoords(); }} onKeyDown={(e) => { if (e.key === "Escape") { e.stopPropagation(); setSearching(false); } }}>
              <span className="ax">X</span>
              <input className="input" inputMode="numeric" aria-label="X" autoFocus value={sx} onChange={(e) => setSx(e.target.value.replace(/[^\d]/g, "").slice(0, 4))} />
              <span className="dia" />
              <span className="ax">Y</span>
              <input className="input" inputMode="numeric" aria-label="Y" value={sy} onChange={(e) => setSy(e.target.value.replace(/[^\d]/g, "").slice(0, 4))} />
              <button type="submit" className="go" aria-label="Go to these coordinates"><IconSearch /></button>
              <button type="button" className="cancel" onClick={() => setSearching(false)}>Cancel</button>
            </form>
          )}
        </div>

        {/* the map tools: a small arrow (down = closed, up = open) over the go-to-city / Layers / Bookmarks discs */}
        <div className={cx("map-side", toolsOpen && "open")}
          onKeyDown={(e) => { if (e.key === "Escape" && toolsOpen && !layersOpen && !marksOpen) { e.stopPropagation(); setToolsOpen(false); toolsToggleRef.current?.focus(); } }}>
          <button type="button" ref={toolsToggleRef} className="map-tools-toggle" aria-label="Map tools" title={toolsOpen ? "Hide the map tools" : "Map tools"}
            aria-expanded={toolsOpen} aria-controls="map-tools" onClick={() => setToolsOpen((o) => !o)}>
            <span className="face"><IconChevronDown /></span>
          </button>
          <div className="map-tools" id="map-tools" inert={!toolsOpen} aria-hidden={!toolsOpen}>
            <Disc size="sm" label="Centre on your city" onClick={goHome}><IconCrosshair /></Disc>
            <Disc size="sm" label="Layers" onClick={() => setLayersOpen(true)}><IconLayers /></Disc>
            <Disc size="sm" label="Bookmarks" onClick={() => setMarksOpen(true)}><IconBookmark /></Disc>
          </div>
        </div>

        {/* marches chip */}
        {soonest && layers.marches && (
          <button type="button" className="map-chip" onClick={() => vid && nav(`/w/${wid}/city/${vid}/rally/movement`)}>
            <span className="disc xs" aria-hidden="true"><IconSwords /></span>
            <span className="txt"><span className="eyebrow">Marches</span><span className="line">{soonest.text}</span></span>
            <IconChevronRight className="chev" />
          </button>
        )}

        {/* realm medallion (region) / legend (realm) */}
        {regime === "region" && ready && (
          <button type="button" className="map-mini" onClick={toRealm} aria-label="Realm view">
            <Minimap world={world.data!} size={size!} view={viewRef} relations={relations} realmColor={realmColor} tick={centre} />
            <span className="cap">Realm</span>
          </button>
        )}
        {regime === "realm" && ready && !layers.guildLegend && (
          <div className="map-legend-stub">
            <button type="button" className="map-legend-show" aria-label="Show guilds on the map" onClick={() => setLayers((l) => ({ ...l, guildLegend: true }))}>
              <span className="dots" aria-hidden="true">{legend.filter((g) => g.gid > 0).slice(0, 3).map((g) => <span key={g.gid} style={{ background: g.color }} />)}</span>
              <span className="cap">Guilds</span><IconChevronUp className="chev" />
            </button>
            <button type="button" className="map-mini inlegend" onClick={toRegion} aria-label="Region view"><span className="cap">Region</span></button>
          </div>
        )}
        {regime === "realm" && ready && layers.guildLegend && (
          <div className="map-legend card" role="group" aria-label="Guilds on the map">
            <div className="head">
              <h3 className="eyebrow">Guilds on the map</h3><span className="hint">{isolate !== null ? "tap again to show all" : "tap to isolate"}</span>
              <button type="button" className="map-legend-hide" aria-label="Hide guilds on the map" title="Hide guilds on the map" onClick={() => setLayers((l) => ({ ...l, guildLegend: false }))}>
                <span className="face"><IconChevronDown /></span>
              </button>
            </div>
            <div className="chips">
              {legend.map((g) => (
                <button key={g.gid} type="button" className={cx("gchip", isolate === g.gid && "on")} onClick={() => { setIsolate((cur) => (cur === g.gid ? null : g.gid)); requestDraw(); }}>
                  <span className="dot" style={{ background: g.color }} />{legendLabel(g.gid)}<span className="n">{g.n}</span>
                </button>
              ))}
              {!legend.length && <span className="muted">No guilds in view.</span>}
            </div>
            <button type="button" className="map-mini inlegend" onClick={toRegion} aria-label="Region view"><span className="cap">Region</span></button>
          </div>
        )}

        {/* the docked city sheet */}
        {sheetTarget && v && (
          <CitySheet
            wid={wid}
            vid={vid!}
            v={v}
            target={sheetTarget}
            home={home}
            relation={selRel ?? "unaligned"}
            guilds={guilds.data}
            bookmarked={bookmarked.has(`${sheetTarget.x},${sheetTarget.y}`)}
            onClose={() => setSelected(null)}
            onGo={(cell) => { flyTo(cell); setSelected(cell); }}
          />
        )}
      </div>

      <LayersSheet open={layersOpen} onClose={() => setLayersOpen(false)} layers={layers} guildMarks={vtOn} onChange={(l) => { setLayers(l); rendererRef.current?.invalidateGround(); requestDraw(); }} />
      <BookmarksSheet open={marksOpen} onClose={() => setMarksOpen(false)} wid={wid} onGo={(cell) => { setMarksOpen(false); flyTo(cell); setSelected(cell); }} />
      <MapCitySwitcher open={switcher} onClose={() => setSwitcher(false)} v={v} wid={wid} vid={vid} onPick={(c) => { setSwitcher(false); const xy = parseCoords(c.coords); if (xy) { flyTo(xy, HOME_SCALE); setSelected(null); } if (c.id !== vid) session.setVillage(c.id); }} />
      {/* phones: swipe the header / nav to the next or previous city — the map flies to it, as picking it from the switcher does */}
      <CitySwipe wid={wid} vid={vid} cities={own}
        onSwitch={(id) => { const c = own.find((x) => x.id === id); const xy = parseCoords(c?.coords); if (xy) { flyTo(xy, HOME_SCALE); setSelected(null); } session.setVillage(id); }} />
    </Page>
  );
}

/** The phone's "<size=80%>(Barbarian)" rich-text tag and the like, out of a city name. */
export const cleanName = (s: string | undefined) => (s ?? "").replace(/<[^>]+>/g, "").replace(/\(Barbarian\)/i, "").trim();

/** Whether the game's "x,y" (with or without spaces) is the cell keyed "x,y". */
function sameCell(coords: string, k: string): boolean {
  const xy = parseCoords(coords);
  return !!xy && `${xy[0]},${xy[1]}` === k;
}

// ------------------------------------------------------------------ the realm medallion's minimap
function Minimap({ world, size, view, relations, realmColor, tick }: { world: WorldMap; size: number; view: React.MutableRefObject<View>; relations: ReturnType<typeof relationsOf>; realmColor: (c: Cell) => string; tick: [number, number] }) {
  const ref = useRef<HTMLCanvasElement>(null);
  const cities = useMemo(() => cityIndex(world), [world]);
  useEffect(() => {
    const c = ref.current;
    if (!c) return;
    const d = 112;
    const dpr = Math.min(2, window.devicePixelRatio || 1);
    c.width = d * dpr; c.height = d * dpr;
    const g = c.getContext("2d")!;
    g.setTransform(dpr, 0, 0, dpr, 0, 0);
    g.fillStyle = "#15130F";
    g.fillRect(0, 0, d, d);
    // the populated part of the world, fitted: find the bounding box of the cities around the view centre
    const vw = view.current;
    const span = Math.min(size, 160);
    const k = d / span;
    const cx0 = vw.cx - span / 2;
    const cy0 = vw.cy + span / 2;
    for (const city of cities.values()) {
      let dx = city.x + 0.5 - cx0; dx = ((dx % size) + size) % size;
      let dy = cy0 - (city.y + 0.5); dy = ((dy % size) + size) % size;
      if (dx > span || dy > span) continue;
      g.fillStyle = relations.own.has(`${city.x},${city.y}`) ? REL_COLORS.you.body! : city.barbarian ? "#4E4A42" : realmColor(city);
      g.fillRect(dx * k - 1, dy * k - 1, 2, 2);
    }
    // the viewport
    const wrapEl = c.parentElement?.parentElement as HTMLElement | null;
    const vwW = (wrapEl?.clientWidth ?? 390) / vw.s;
    const vwH = (wrapEl?.clientHeight ?? 600) / vw.s;
    g.strokeStyle = "#EDD69D";
    g.lineWidth = 1.2;
    g.fillStyle = "rgba(237,214,157,0.12)";
    g.beginPath();
    g.rect(d / 2 - (vwW * k) / 2, d / 2 - (vwH * k) / 2, vwW * k, vwH * k);
    g.fill(); g.stroke();
  }, [cities, size, view, relations, realmColor, tick]);
  return <canvas ref={ref} className="mini-canvas" aria-hidden="true" />;
}
